import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Workflow, UserCheck, ArrowRight } from 'lucide-react';

const DISMISSED_KEY = 'ac_onboarding_guidance_dismissed';

export function hasSeenGuidance(): boolean {
  return localStorage.getItem(DISMISSED_KEY) === 'true';
}

interface CustomerGuidanceModalProps {
  open: boolean;
  onDismiss: () => void;
}

export function CustomerGuidanceModal({ open, onDismiss }: CustomerGuidanceModalProps) {
  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    onDismiss();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleDismiss(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg">Getting started with Customers</DialogTitle>
          <DialogDescription>
            There are two ways to assign strategies and credit controllers to your customers.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-1">

          {/* Path 1 — Bulk */}
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Workflow className="h-3.5 w-3.5" />
              </div>
              <p className="text-sm font-semibold">Quick setup — bulk actions</p>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Select one or more customers using the checkboxes, then click the{' '}
              <span className="font-medium text-foreground">Actions</span> button to assign a{' '}
              <span className="font-medium text-foreground">Collection Strategy</span> or{' '}
              <span className="font-medium text-foreground">Credit Controller</span> to all of them at once.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
            <div className="flex-1 h-px bg-border" />
            <span>or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Path 2 — Individual */}
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <UserCheck className="h-3.5 w-3.5" />
              </div>
              <p className="text-sm font-semibold">Full control — customer detail</p>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Click any{' '}
              <span className="font-medium text-foreground">customer name</span> to open their details, then go to the{' '}
              <span className="font-medium text-foreground">Overview tab</span> and scroll down to the{' '}
              <span className="font-medium text-foreground">Collections Settings</span> area to assign a strategy, risk level, credit controller, and account manager individually.
            </p>
          </div>

        </div>

        <DialogFooter className="mt-1">
          <Button onClick={handleDismiss} className="w-full sm:w-auto">
            Got it <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
